import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth';
import { calculateProcessingFees } from 'utils/helpers';
import { useAlert, useSelectedCloverDevice } from 'hooks';
import {
  Icon,
  Flex,
  Button,
  TerminalApprove,
  TerminalDecline,
  TerminalCompletePurchase,
} from 'components';

import { useRemainderCart } from '../hooks';
import { TemplateDialog } from '../template-dialog';

import { CreditProps } from './types';
import { useCreditPayment } from './use-credit-payment';

export const Credit = ({
  event,
  amount,
  checkoutId,
  isProcessingFeeEnabled,
  onAddCreditPaymentMethod,
}: CreditProps) => {
  const { showErrorAlert } = useAlert();
  const authUser = useAppSelector(selectAuthUser);
  const { remainder, isBalanceZero } = useRemainderCart(
    amount,
    isProcessingFeeEnabled
  );

  const { selectedDeviceId: deviceId } = useSelectedCloverDevice(event);
  const processingFeeAmount = calculateProcessingFees(
    remainder,
    isProcessingFeeEnabled ? Number(authUser?.company?.processingFee) : 0
  );

  const {
    paymentStatus,
    onCancelPayment,
    isCancelDisabled,
    showPaymentDialog,
    handleCreditPayment,
    setShowPaymentDialog,
    paymentPendingAnimation,
    handleAnimationComplete,
  } = useCreditPayment({
    checkoutId,
    deviceId: deviceId!,
    creditAmounts: remainder,
    eventId: Number(event?.id),
    processingFee: processingFeeAmount,
    onSuccessPayment: onAddCreditPaymentMethod,
  });

  const onOpen = () => {
    if (!deviceId) {
      return showErrorAlert(
        'No device added for event. Contact the company owner to add a device'
      );
    }

    if (isBalanceZero) {
      return showErrorAlert(
        'The balance is zero, it is unable to make a payment by card'
      );
    }

    handleCreditPayment();
  };

  return (
    <TemplateDialog
      onOpen={onOpen}
      triggerTitle="Credit"
      icon={<Icon.Credit />}
      open={showPaymentDialog}
      closeOnDocumentClick={false}
      title="Complete purchase on terminal"
      onClose={() => setShowPaymentDialog(false)}
    >
      {(close) => {
        if (paymentStatus === 'idle') {
          return (
            <Flex column gap={16} fullWidth alignItems="center">
              <TerminalCompletePurchase onComplete={handleAnimationComplete} />
              <Button
                fullWidth
                text="Cancel payment"
                onClick={onCancelPayment}
                colorType={ButtonColors.LightRed}
                disabled={isCancelDisabled || paymentPendingAnimation}
              />
            </Flex>
          );
        }

        if (paymentStatus === 'failure') {
          return <TerminalDecline onComplete={close} />;
        }

        if (paymentStatus === 'success') {
          return <TerminalApprove onComplete={close} />;
        }
      }}
    </TemplateDialog>
  );
};
